import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import FileTreeView from "../../data-wrangler/components/AdvanceDataWrangler/FileTreeView";
import React, { useMemo, useCallback } from "react";
import { getBidsDerivatives, getBidsEntities, getBidsFiles, getPipelines } from "../../data-wrangler/UtilityFunctions";
import { useWizardQuery } from "../../../api/dataWranglerApiSlice";
import { useSelector } from "react-redux";
import { selectSelectedDatabase } from "../../common/databaseSlice";
import { getEnumKeyByEnumValue } from "../../../utils/index";
import { useUserInfoQuery } from "../../../api/userApiSlice";

type RawDataType = {
    data_type: string,
    entity_options: object,
    suffix: string,
    format_options: (string|string[])[]
    modality: string,
    description: string,
    BIDS_file_type: string,
    file_dataset: string
}

enum BIDSFileType {
    Metadata = "metadata",
    Acquisition = "acquisition",
}

enum DerivativeType {
    Raw = "raw",
    Source = "sourcedata",
}

type props = {
    study: string;
};

const ImagingFiles = ({ study }: props) => {
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data: userData } = useUserInfoQuery({});
    const isGuestUser = userData?.id === -1
    const { data, isLoading, isFetching } = useWizardQuery({ database: selectedDatabase }, { skip: isGuestUser });
    const treeUIIDs = useMemo(() => new Set<string>(), [data]);
    const treeNodeById = useMemo(() => new Map<string, any>(), [data]);

    const getChildrenTree = useCallback(((node: any, id: string, selectionPath: string[]) : any => {
        const nodeCommon: any = {};
        // Check that label is not a group
        if (node.label && typeof node.label == 'string') {
            nodeCommon.humanReadableLabel = node.label;
        }
        // if node.source_suffix is NULL, description applies to the first level group
        // if typeof node.description is not a string, description is a group
        if (
            node.description
            && node.source_suffix !== null
            && typeof node.description == 'string'
        ) {
            nodeCommon.description = node.description;
        }

        if ('suffix' in node) {
            const suffix = node.virtual_suffix || node.suffix || "[no_suffix]";
            const virtualGroups = (node.virtual_groups || []).map((group: string) => `virtual_groups::${group}`);
            const groups = (node.groups || []).map((group: string) => `group::${group}`);
            const nodeId = `${id}__${suffix}`;
            const altPath = virtualGroups.length ? [
                ...selectionPath,
                ...groups,
                `suffix::${node.suffix || "[no_suffix]"}`,
                ...(node.add_extension_to_selectionPath && node.format_options?.length
                    ? [`extension::${node.format_options.join(",")}`]
                    : [])
            ] : null;

            if (treeNodeById.has(nodeId)) {
                const existing = treeNodeById.get(nodeId);
                if (
                    !existing.altPaths.some(
                        (p: string[]) => JSON.stringify(p) === JSON.stringify(altPath)
                    )
                    && altPath
                ) {
                    existing.altPaths.push(altPath);
                }
                return null;
            }

            if (!treeUIIDs.has(nodeId)) {
                treeUIIDs.add(nodeId);
                const tree = {
                    ...nodeCommon,
                    id: nodeId,
                    selectionPath: [
                        ...selectionPath,
                        ...(virtualGroups.length ? virtualGroups : groups),
                        `suffix::${node.suffix || "[no_suffix]"}`,
                        ...(node.add_extension_to_selectionPath && node.format_options?.length
                            ? [`extension::${node.format_options.join(",")}`]
                            : [])
                    ],
                    altPaths: altPath ? [altPath] : [],
                    type: 'file_suffix',
                    description: node.description,
                    groups: node.groups,
                    formats: node.format_options,
                    label: suffix,
                    parentId: id,
                };
                treeUIIDs.add(nodeId);
                treeNodeById.set(nodeId, tree);
                return tree;
            }
        } else {
            return Object.keys(node || {})
                .filter((label) => {
                    const isNumericKey = /^\d+$/.test(label);
                    if (isNumericKey) return true;
                    const nodeId = id ? `${id}__${label}` : label;
                    if (!treeUIIDs.has(nodeId)) {
                        treeUIIDs.add(nodeId);
                        return true;
                    }
                    return false;
                })
                .map((label: any) => {
                    const isNumericKey = /^\d+$/.test(label);
                    if (isNumericKey) {
                        return getChildrenTree(node[label], id, selectionPath);
                    } else if (label !== 'description') {
                        const nodeId = id ? `${id}__${label}` : label;
                        const tree: any = {
                            ...nodeCommon,
                            id: nodeId,
                            label: label,
                            parentId: id,
                            description: node[label]?.description,
                        }

                        // if any direct child with display_folder_only
                        const firstChild = node[label]?.[0];
                        if (firstChild?.display_folder_only) {
                            const virtualGroups = (firstChild.virtual_groups || []).map((group: string) => `virtual_groups::${group}`);
                            const groups = (firstChild.groups || []).map((group: string) => `group::${group}`);

                            tree.selectionPath = [
                                ...selectionPath,
                                ...(virtualGroups.length ? virtualGroups : groups),
                                ...(firstChild.add_extension_to_selectionPath && firstChild.format_options?.length
                                    ? [`extension::${firstChild.format_options.join(",")}`]
                                    : [])
                            ];
                            tree.altPaths = virtualGroups.length ? [[
                                ...selectionPath,
                                ...groups,
                                ...(firstChild.add_extension_to_selectionPath && firstChild.format_options?.length
                                    ? [`extension::${firstChild.format_options.join(",")}`]
                                    : [])
                            ]] : [];
                        } else {
                            const children = getChildrenTree(node[label], nodeId, selectionPath).flat().filter(Boolean);
                            if (children.length > 0) {
                                tree.children = children;
                            }
                        }
                        return tree;
                    }
                });
        }
    }), [treeUIIDs]);

    const fileTypeOptions = useMemo(() => {
        if (!data?.bids_files) return [];

        const datasetFileTypeBidsFiles = getBidsFiles(data?.bids_files);
        if (!datasetFileTypeBidsFiles) return [];

        const options: any = {};
        data?.file_datasets.forEach(({ name: dataset }: {name: string}) => {
            options[dataset] = [];
            Object.keys(datasetFileTypeBidsFiles[dataset] || {}).forEach((fileType: string) => {
                options[dataset][fileType] = Object.keys(datasetFileTypeBidsFiles[dataset][fileType] || {}).map((dataType: string) => {
                    let globalEntitiesList: any = [];
                    const dataTypeFiles = datasetFileTypeBidsFiles[dataset][fileType][dataType];
                    const dataTypeWithMetadata = dataTypeFiles.some(
                        (dataTypeFile: RawDataType) => dataTypeFile.BIDS_file_type === BIDSFileType.Metadata
                    );

                    const children = dataTypeFiles.reduce((acc: any[], file: any) => {
                        const bidsEntities = getBidsEntities(datasetFileTypeBidsFiles[dataset][fileType][dataType], file.suffix);
                        const suffix = file.suffix || "[no_suffix]";
                        const entitiesList = Array.from(bidsEntities.keys()).map((entity) =>
                            ({
                                id: `${dataset}__raw__${dataType}__${suffix}__${entity}`,
                                selectionPath: [dataset, 'raw', dataType, suffix, entity],
                                type: 'entity',
                                label: entity,
                                entityValues: [...bidsEntities.get(entity)],
                                parentId: `${dataset}__raw__${dataType}__${suffix}`,
                            })
                        );

                        // has metadata
                        if (dataTypeWithMetadata && file.BIDS_file_type === BIDSFileType.Acquisition) {
                            globalEntitiesList = entitiesList.map((entity) => {
                                return {
                                    ...entity,
                                    selectionPath: [
                                        ...entity.selectionPath.slice(0, 3),
                                        '_entities_',
                                        ...entity.selectionPath.slice(4),
                                    ]
                                }
                            });
                        }

                        const nodeId = `${dataset}__raw__${dataType}__${suffix}`;
                        if (!treeUIIDs.has(nodeId)) {
                            treeUIIDs.add(nodeId);
                            acc.push({
                                id: nodeId,
                                selectionPath: [dataset, 'raw', dataType, suffix],
                                type: 'file_suffix',
                                formats: file.format_options,
                                label: suffix,
                                humanReadableLabel: file.label,
                                description: file.description,
                                children: !dataTypeWithMetadata ? entitiesList : [],
                                parentId: `${dataset}__raw__${dataType}`,
                            });
                        }
                        return acc;
                    }, [])

                    if (dataType === 'null') {
                        return children;
                    }

                    return {
                        id: `${dataset}__raw__${dataType}`,
                        type: 'data_type',
                        label: dataType,
                        description: dataTypeFiles.description || null,
                        children: [
                            ...globalEntitiesList,
                            ...children,
                        ],
                        parentId: null,
                    }
                }).flat();
            })
        });
        return options;
    }, [data, getChildrenTree]);

    const derivativesOptions = useMemo(() => {
        if (!data?.bids_derivative_files) return [];
        const pipelinesData: any = getPipelines(data?.bids_derivative_files);

        const pipelinesOptions: any = {};
        Object.entries(pipelinesData).forEach(([dataset, pipelinesTypes]: [string, any]) => {
            pipelinesOptions[dataset] = [];
            Object.entries(pipelinesTypes).forEach(([derivativeType, pipelines]: [string, any]) => {
                pipelinesOptions[dataset].push(...Object.keys(pipelines || {}).map((pipeline: any) => {
                    const id = `${dataset}_pipelines__${pipeline}`;
                    const selectionPath = [dataset, 'pipelines', pipeline];
                    const tree: any = {
                        id: id,
                        type: 'pipeline',
                        label: pipeline,
                        parentId: null,
                        description: pipelines[pipeline].description,
                        category: getEnumKeyByEnumValue(DerivativeType, derivativeType),
                    };
                    const children = getChildrenTree(pipelines[pipeline], id, selectionPath)
                        .flat().filter(Boolean);
                    if (children.length > 0) {
                        tree.children = children;
                    }
                    return tree;
                }));
            });
        })
        return pipelinesOptions;
    }, [data]);

    const sourcedataDatasetsOptions: any = useMemo(() => {
        if (!data?.bids_sourcedata) return [];
        const options: any = {};

        data?.file_datasets.forEach(({ name: dataset }: {name: string}) => {
            const datasetSourcedata = data?.bids_sourcedata.filter((node: any) => node.file_dataset === dataset);
            const datatypes = getBidsDerivatives(datasetSourcedata);

            options[dataset] = [
                ...(datatypes['null'] || []).map((file: any) => {
                    return getChildrenTree(file, `${dataset}_sourcedata`, [dataset, 'sourcedata']);
                }),
                ...Object.keys(datatypes || {})
                    .sort()
                    .filter((datatype) => datatype && datatype !== 'null')
                    .map((datatype) => {
                        const id = `${dataset}_sourcedata__${datatype}`;
                        const selectionPath = [dataset, 'sourcedata', datatype];
                        const tree: any = {
                            id: id,
                            type: 'data_type',
                            label: datatype,
                            parentId: null,
                        };
                        const children = getChildrenTree(datatypes[datatype], id, selectionPath)
                            .flat().filter(Boolean);
                        if (children.length > 0) {
                            tree.children = children;
                        }
                        return tree;
                    })
            ]
        });

        return options;
    }, [data]);


    const optionsAvailable =
           !!Object.keys(derivativesOptions?.[study] || {}).length
        || !!Object.keys(sourcedataDatasetsOptions?.[study] || {}).length
        || !!Object.keys(fileTypeOptions?.[study] || {}).length;

    return (
        <Stack height="100%">
            {(isLoading || isFetching) && <CircularProgress/>}
            {(!isLoading && !isFetching && !optionsAvailable) && <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{ height: "300px" }}
            >
                No Files Available
            </Stack>}

            {!isLoading && !isFetching && optionsAvailable &&
                <Stack flexDirection="row" height="100%" justifyContent="space-between" gap={2}>
                    {fileTypeOptions?.[study]?.["imaging"]?.length > 0 &&
                        <Stack flexDirection="row" height="100%" justifyContent="space-between" flexGrow="1"
                            flexBasis="auto" flexShrink="0" width="30%">
                            <Stack flexDirection="column" height="100%" flexGrow="1">
                                <Typography variant="h5" sx={{ marginLeft: "10px", marginBottom: "15px" }}>
                                    Raw
                                </Typography>
                                <Stack height="100%" overflow="auto">
                                    <FileTreeView options={fileTypeOptions?.[study]?.["imaging"]} />
                                </Stack>
                            </Stack>
                            <Divider orientation="vertical" flexItem variant="fullWidth" sx={{ borderRightWidth: 2 }}/>
                        </Stack>
                    }

                    <Stack flexGrow="1" height="100%" flexDirection="row" flexBasis="auto" flexShrink="0" width="30%">
                        <Stack flexGrow="1" flexBasis="auto" flexShrink="0">
                            <Typography variant="h5" sx={{ marginLeft: "10px", marginBottom: "15px" }}>
                                Derivatives
                            </Typography>
                            <Stack height="100%" overflow="auto">
                                <FileTreeView options={derivativesOptions?.[study]} />
                            </Stack>
                        </Stack>
                        {sourcedataDatasetsOptions?.[study]?.length !== 0 &&
                            <Divider orientation="vertical" flexItem variant="fullWidth"
                                sx={{ borderRightWidth: 2, marginLeft: "10px" }}/>
                        }
                    </Stack>

                    {sourcedataDatasetsOptions?.[study]?.length !== 0 &&
                        <Stack height="100%" flexDirection="row" justifyContent="space-between" flexGrow="1" flexBasis="auto" flexShrink="0" width="30%">
                            <Stack>
                                <Typography variant="h5" sx={{ marginLeft: "10px", marginBottom: "15px" }}>
                                    Source
                                </Typography>
                                <Stack height="100%" overflow="auto">
                                    <FileTreeView options={sourcedataDatasetsOptions?.[study]} />
                                </Stack>
                            </Stack>
                        </Stack>
                    }
                </Stack>
            }
        </Stack>
    )
}

export default ImagingFiles